import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  fetchBatchDetail,
  createBatch,
  updateBatch,
} from "./adminCourseService";
import { fetchActiveSpeakers } from "../services/adminSpeakerServices";
import "./adminCourses.css";

const TIMEZONES = [
  "Asia/Kolkata",
  "Asia/Dubai",
  "America/New_York",
  "America/Los_Angeles",
  "Europe/London",
  "Europe/Paris",
  "Australia/Sydney",
  "Asia/Singapore",
];

const EMPTY = {
  batchName: "",
  mode: "BATCH",
  startDate: "",
  dailySessionTime: "",
  timezone: "Asia/Kolkata",
  instructorId: "",
  minCapacity: 4,
  maxCapacity: 6,
  notes: "",
};

const AdminBatchFormPage = () => {
  const { id: courseId, batchId } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(batchId);

  const [form, setForm] = useState(EMPTY);
  const [speakers, setSpeakers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const init = async () => {
      try {
        const speakerRes = await fetchActiveSpeakers();
        setSpeakers(speakerRes.data || []);

        if (isEdit) {
          const batchRes = await fetchBatchDetail(courseId, batchId);
          const b = batchRes.data;
          setForm({
            batchName: b.batchName || "",
            mode: b.mode || "BATCH",
            startDate: b.startDate || "",
            dailySessionTime: b.dailySessionTime || "",
            timezone: b.timezone || "Asia/Kolkata",
            instructorId: b.instructorId || "",
            minCapacity: b.minCapacity ?? 4,
            maxCapacity: b.maxCapacity ?? 6,
            notes: b.notes || "",
          });
        }
      } catch {
        setError("Failed to load data.");
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [courseId, batchId, isEdit]);

  const set = (k, v) =>
    setForm((prev) => {
      const next = { ...prev, [k]: v };
      // lock capacity to 1 for PERSONAL mode
      if (k === "mode" && v === "PERSONAL") {
        next.minCapacity = 1;
        next.maxCapacity = 1;
      }
      return next;
    });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!form.instructorId) {
      setError("Please select an instructor.");
      return;
    }

    const payload = {
      batchName: form.batchName,
      mode: form.mode,
      startDate: form.startDate,
      dailySessionTime: form.dailySessionTime,
      timezone: form.timezone,
      instructorId: Number(form.instructorId),
      minCapacity: form.mode === "PERSONAL" ? 1 : Number(form.minCapacity),
      maxCapacity: form.mode === "PERSONAL" ? 1 : Number(form.maxCapacity),
      notes: form.notes,
    };

    setSaving(true);
    try {
      if (isEdit) {
        await updateBatch(courseId, batchId, payload);
      } else {
        await createBatch(courseId, payload);
      }
      navigate(`/admin/courses/${courseId}/batches`);
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data ||
        "Save failed. Please try again.";
      setError(String(msg));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="ac-loading">Loading…</div>;

  const isPersonal = form.mode === "PERSONAL";

  return (
    <div>
      <div className="ac-header">
        <h2>{isEdit ? "Edit Batch" : "New Batch"}</h2>
        <button
          className="ac-btn ac-btn-ghost"
          onClick={() => navigate(`/admin/courses/${courseId}/batches`)}
        >
          ← Batches
        </button>
      </div>

      <form className="ac-form-card" onSubmit={handleSubmit}>
        {error && (
          <div style={{
            background: "#fee2e2", color: "#b91c1c",
            padding: "0.6rem 0.9rem", borderRadius: "6px",
            marginBottom: "1rem", fontSize: "0.88rem",
          }}>
            {error}
          </div>
        )}

        {/* Batch name + mode */}
        <div className="ac-form-row">
          <div className="ac-form-group">
            <label>Batch Name *</label>
            <input
              required
              value={form.batchName}
              onChange={(e) => set("batchName", e.target.value)}
              placeholder="e.g. Batch A – Feb 2025"
            />
          </div>
          <div className="ac-form-group">
            <label>Mode *</label>
            <select value={form.mode} onChange={(e) => set("mode", e.target.value)}>
              <option value="BATCH">Group (Batch)</option>
              <option value="PERSONAL">Personal (1-on-1)</option>
            </select>
          </div>
        </div>

        {/* Start date + daily time */}
        <div className="ac-form-row">
          <div className="ac-form-group">
            <label>Start Date *</label>
            <input
              type="date"
              required
              value={form.startDate}
              onChange={(e) => set("startDate", e.target.value)}
            />
          </div>
          <div className="ac-form-group">
            <label>Daily Session Time *</label>
            <input
              type="time"
              required
              value={form.dailySessionTime}
              onChange={(e) => set("dailySessionTime", e.target.value)}
            />
          </div>
        </div>

        {/* Timezone + instructor */}
        <div className="ac-form-row">
          <div className="ac-form-group">
            <label>Timezone *</label>
            <select value={form.timezone} onChange={(e) => set("timezone", e.target.value)}>
              {TIMEZONES.map((tz) => (
                <option key={tz} value={tz}>{tz}</option>
              ))}
            </select>
          </div>
          <div className="ac-form-group">
            <label>Instructor *</label>
            <select
              value={form.instructorId}
              onChange={(e) => set("instructorId", e.target.value)}
              required
            >
              <option value="">— Select instructor —</option>
              {speakers.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Capacity — locked for PERSONAL */}
        <div className="ac-form-row">
          <div className="ac-form-group">
            <label>Min Capacity</label>
            <input
              type="number" min={1}
              value={form.minCapacity}
              disabled={isPersonal}
              onChange={(e) => set("minCapacity", e.target.value)}
              style={isPersonal ? { background: "#f3f4f6", cursor: "not-allowed" } : {}}
            />
            {isPersonal && (
              <span className="ac-form-error">Locked to 1 for Personal mode</span>
            )}
          </div>
          <div className="ac-form-group">
            <label>Max Capacity</label>
            <input
              type="number" min={1}
              value={form.maxCapacity}
              disabled={isPersonal}
              onChange={(e) => set("maxCapacity", e.target.value)}
              style={isPersonal ? { background: "#f3f4f6", cursor: "not-allowed" } : {}}
            />
          </div>
        </div>

        {/* Notes */}
        <div className="ac-form-group">
          <label>Notes</label>
          <textarea
            rows={3}
            value={form.notes}
            onChange={(e) => set("notes", e.target.value)}
            placeholder="Internal notes (optional)"
          />
        </div>

        <div className="ac-form-actions">
          <button type="submit" className="ac-btn ac-btn-success" disabled={saving}>
            {saving ? "Saving…" : isEdit ? "Update Batch" : "Create Batch"}
          </button>
          <button
            type="button"
            className="ac-btn ac-btn-ghost"
            onClick={() => navigate(`/admin/courses/${courseId}/batches`)}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default AdminBatchFormPage;
